import * as Analyzer from '../analyzer';
import { AstNode, CommandByteSequence, Description, RuleTree, RuleTreeContent } from '../types';
import { checkChunkArgs, findRuleTree, generateRuleTree } from './argrule';
import { extractData } from './dataextraction';
import { getDataRule } from './datarule';
import { convertToConsoleHexString } from './util';

export class ParserManager {
  #parsedChunk: AstNode[];
  #commandTree: RuleTree;

  constructor() {
    this.#commandTree = generateRuleTree();
  }

  parseRawData(rawData: string[] | string, shouldOptimize = true): { ast: AstNode[]; possibleMaxWidth: number } {
    this.#parsedChunk = [];

    let tempMatchedBuffer: string[] = [];
    let tempNormalBytesBuffer: string[] = [];
    let parsingPosition = 0;

    if (typeof rawData === 'string') rawData = rawData.split('');

    const parseCurrentNode = (node: RuleTreeContent): boolean => {
      if (Object.keys(node).length === 1 && 'content' in node) {
        /*
         * Last 1 byte of argument + 1 more byte turned out to be another command, so
         * treat current command as having one less argument/parameter which mean they
         * will use the default arg value for the last arg (if any, and error if not)
         *
         * Note: this check is possible only because a command byte sequence
         *       will never have two same consecutive bytes e.g. 0x1b1b
         */
        let ignoreArgs = 0;
        const initialArgs = node.content.fixedArgumentLength ?? 0;
        if (initialArgs > 0 && Object.keys(findRuleTree(this.#commandTree, peek(2, initialArgs - 1))).length) {
          // There could only be one optional argument which always
          // be the last arg in a command in this case
          ignoreArgs = 1;
        }

        const currentChunk = this.parseChunk({
          command: tempMatchedBuffer.join(''),
          commandDescription: node.content,
          rawData: [...rawData.slice(parsingPosition)],
          ignoreArgs,
        });

        if (tempNormalBytesBuffer.length > 0) {
          /**
           * We will always have to parse textual data everytime a command found,
           * this is because some ESC/POS format dont use linefeed command to print
           * data in the print buffer which is the textual data but instead the
           * printer will always print everything directly whenever it found textual
           * data or data that could be decoded from the chosen charset
           */
          currentChunk.extractedData = {
            text: tempNormalBytesBuffer,
            ...currentChunk.extractedData,
          };
        }

        this.#parsedChunk.push(currentChunk);

        tempMatchedBuffer = [];
        if (currentChunk.rawData) {
          parsingPosition += currentChunk.rawData.length;
        }

        return true;
      }

      return false;
    };

    const peek = (byteSize: number, skipByte = 0) => {
      return [...rawData.slice(parsingPosition + skipByte, parsingPosition + byteSize + skipByte)];
    };

    while (parsingPosition < rawData.length) {
      const byte: string = rawData[parsingPosition];

      switch (tempMatchedBuffer.length) {
        case 0:
          if (this.#commandTree[byte]) {
            tempMatchedBuffer = [byte];
            parsingPosition++;
            break;
          }

          if (tempMatchedBuffer.length === 0) {
            /**
             * Current byte is possibly a normal byte so parse
             * it as an ascii printable string
             *
             * Note: we need to handle multiple unicode encoding
             *       with different charsets/codec but for this we
             *       need a realworld printed receipt example first
             */
            tempNormalBytesBuffer.push(byte);
            parsingPosition++;
          }
          break;

        default: {
          const currentCommandTree = findRuleTree(this.#commandTree, tempMatchedBuffer);

          let matchedCommandFound = parseCurrentNode(currentCommandTree);
          let matchedByteFound = false;

          if (!matchedCommandFound) {
            if (currentCommandTree[byte]) {
              tempMatchedBuffer.push(byte);
              parsingPosition++;

              matchedCommandFound = parseCurrentNode(currentCommandTree[byte]);
              matchedByteFound = true;
            }
          }

          // Normal bytes have been parsed as textual data so clear
          // and reset the buffer
          if (matchedCommandFound && this.#parsedChunk[this.#parsedChunk.length - 1].extractedData?.text) {
            tempNormalBytesBuffer = [];
          }

          /**
           * Current parsed byte sequence has no matching command
           * so, skip current parsed byte and jump to the next one
           *
           * Note: keep in mind that this is also the case for
           *       unimplemented command in the parser rule
           */
          if (!matchedCommandFound && !matchedByteFound) {
            console.log(`[Warning]: there are no command with [${convertToConsoleHexString(tempMatchedBuffer.join('').concat(byte))}] byte sequence`);

            tempMatchedBuffer = [];
            parsingPosition++;
          }
          break;
        }
      }
    }

    // Parse all remaining normal bytes if any
    if (tempNormalBytesBuffer.length > 0) {
      this.#parsedChunk.push({
        command: '',
        description: '',
        isCorrected: false,
        extractedData: {
          text: tempNormalBytesBuffer,
        },
      });
    }

    if (shouldOptimize) {
      return Analyzer.fixParsedEscPosAST(this.#parsedChunk, this.#commandTree);
    } else {
      return {
        ast: this.#parsedChunk,
        possibleMaxWidth: -1,
      };
    }
  }

  parseChunk({
    command,
    commandDescription,
    rawData,
    ignoreArgs,
  }: {
    command: CommandByteSequence;
    commandDescription: Description;
    rawData: string[];
    ignoreArgs: number;
  }): AstNode {
    const { description: title, fixedArgumentLength: initialArgs } = commandDescription;
    const chunkData: AstNode = {
      command,
      description: title,
      isCorrected: false,
    };

    if (initialArgs && initialArgs > 0) {
      if (rawData.length < Math.min(0, initialArgs - ignoreArgs)) {
        throw new Error(`Expected args for [${convertToConsoleHexString(command)}] command but received EOF`);
      }

      const args = rawData.slice(0, initialArgs);

      let chunkDataParsedArgs = [];
      if (ignoreArgs === 0) {
        chunkDataParsedArgs = checkChunkArgs(
          command,
          rawData,
          args.map((val) => val.charCodeAt(0))
        );
      } else if (ignoreArgs < initialArgs) {
        chunkDataParsedArgs = checkChunkArgs(
          command,
          rawData,
          args.slice(0, args.length - ignoreArgs).map((val) => val.charCodeAt(0))
        );
      } else {
        chunkDataParsedArgs = checkChunkArgs(command, rawData);
      }

      chunkData.parsedArgs = chunkDataParsedArgs;
      if (chunkData.parsedArgs.length > 0) {
        chunkData.rawData = rawData.slice(0, chunkData.parsedArgs.length - ignoreArgs);
      }
    }

    const dataRuleFunction = getDataRule(command);
    if (dataRuleFunction) {
      const additionalData = dataRuleFunction(rawData);

      if (additionalData) {
        if ((additionalData.parsedArgs[0] as number[]).length > 0)
          if (chunkData.parsedArgs) chunkData.parsedArgs?.push(...additionalData.parsedArgs);
          else chunkData.parsedArgs = [...additionalData.parsedArgs];

        if (chunkData.rawData) chunkData.rawData?.push(...additionalData.rawData);
        else chunkData.rawData = [...additionalData.rawData];
      }
    }

    const maybeExtracted = extractData(command, chunkData.parsedArgs);
    if (maybeExtracted) chunkData.extractedData = maybeExtracted?.extractedData;

    return chunkData;
  }

  printAST() {
    if (this.#parsedChunk) console.log(JSON.stringify(this.#parsedChunk, null, 2));
    else console.log('[Warning]: parsed chunk is empty, please call parseRawData() first');
  }
}
